// scoringApi.js - 서술형 채점 API 서비스

import { auth } from '../firebase/config';
import { getApiUrl } from '../utils/apiConfig';
import { fetchWithRetry, calcApiTimeout } from '../utils/fetchWithRetry';

// 인증 헤더 생성
const getAuthHeaders = async () => {
  const user = auth.currentUser;
  
  if (!user) {
    return {
      'Content-Type': 'application/json',
    };
  }

  try {
    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  } catch (error) {
    console.error('토큰 가져오기 실패:', error);
    return {
      'Content-Type': 'application/json',
    };
  }
};

// API 요청 헬퍼 함수
const apiRequest = async (url, options = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(getApiUrl(url), {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

const apiRequestWithRetry = async (url, options = {}, retryConfig = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetchWithRetry(getApiUrl(url), {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    }, retryConfig);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// 채점 근거 위치(제출물 위 좌표) 계산 요청. 서버가 요청이 열린 동안 OCR·매칭을 수행하므로 시간이 걸린다.
// 실패해도 채점 결과에는 영향이 없으므로 호출 측은 보통 await 하지 않는다.
export const computeScoringEvidenceAnchors = async (recordId, { resultIndexes, force } = {}) => {
  if (!recordId) return null;
  const body = {};
  if (Array.isArray(resultIndexes) && resultIndexes.length > 0) body.result_indexes = resultIndexes;
  if (force) body.force = true;
  const timeoutMs = 60_000 + 20_000 * Math.max(1, Array.isArray(resultIndexes) ? resultIndexes.length : 30);
  return await apiRequestWithRetry(
    `/api/scoring/history/${recordId}/anchors`,
    { method: 'POST', body: JSON.stringify(body) },
    { maxRetries: 0, timeoutMs: Math.min(timeoutMs, 15 * 60_000) },
  );
};

export const requestEvidenceAnchorsInBackground = (recordId, resultIndexes) => {
  if (!recordId) return;
  computeScoringEvidenceAnchors(recordId, { resultIndexes }).catch((error) => {
    console.warn('[scoring] 근거 위치 계산 요청 실패(무시):', error?.message || error);
  });
};

// 채점 근거 위치 조회: { success, enabled, anchors: { [resultIndex]: doc } }
export const getScoringEvidenceAnchors = async (recordId) => {
  return await apiRequest(`/api/scoring/history/${recordId}/anchors`);
};

// 근거 하이라이트용 페이지 이미지(JPEG). <img>는 Authorization 헤더를 못 보내므로 fetch 후 object URL로 돌려준다.
// 호출자가 URL.revokeObjectURL 로 해제해야 한다.
// maxSide 를 비우면 서버의 표시 기본 크기(선업로드 캐시와 동일) 를 그대로 받는다.
export const fetchScoringEvidencePageImage = async (recordId, resultIndex, page, maxSide = 0) => {
  const authHeaders = await getAuthHeaders();
  const query = maxSide ? `?max_side=${maxSide}` : '';
  const response = await fetch(
    getApiUrl(`/api/scoring/history/${recordId}/file/${resultIndex}/page/${page}.jpg${query}`),
    { headers: { Authorization: authHeaders.Authorization || '' } },
  );
  if (!response.ok) {
    throw new Error(`페이지 이미지를 불러오지 못했어요 (${response.status})`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

// 채점 결과 저장
export const saveScoringRecord = async (data) => {
  const response = await apiRequest('/api/scoring/save', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  // 저장 직후 근거 위치 계산을 백그라운드로 요청한다(응답을 기다리지 않음).
  if (response?.record_id) {
    requestEvidenceAnchorsInBackground(response.record_id);
  }
  return response;
};

// 채점 기록 목록 조회
export const getScoringHistory = async (limit = 100, offset = 0, isPro = false, query = '') => {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    is_pro: String(isPro),
  });
  if (query && query.trim()) {
    params.set('q', query.trim());
  }
  return await apiRequest(`/api/scoring/history?${params.toString()}`);
};

// 채점 기록 상세 조회
export const getScoringRecord = async (recordId) => {
  return await apiRequest(`/api/scoring/history/${recordId}`);
};

// 엑셀 다운로드용 채점 기록 상세 일괄 조회
export const getScoringHistoryExportData = async (recordIds) => {
  return await apiRequest('/api/scoring/history/export-data', {
    method: 'POST',
    body: JSON.stringify({ record_ids: recordIds }),
  });
};

// 채점 기록 삭제
export const deleteScoringRecord = async (recordId) => {
  return await apiRequest(`/api/scoring/history/${recordId}`, {
    method: 'DELETE',
  });
};

// 채점 결과 필드 수정 (교사의 총평, 교과세특 등)
export const updateScoringResult = async (recordId, resultIndex, updates) => {
  const response = await apiRequest(`/api/scoring/history/${recordId}/result/${resultIndex}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  // 항목 점수·문항 결과가 바뀌면(AI 수정, 점수 수정) 인용문·판정이 달라질 수 있어 근거 위치를 다시 계산한다.
  // 서버는 인용문+판정 서명이 같으면 재사용하고, 바뀌었으면 OCR 캐시로 재매칭한다(Vision 재호출 없음).
  if (updates && (updates.item_scores !== undefined || updates.question_results !== undefined)) {
    requestEvidenceAnchorsInBackground(recordId, [resultIndex]);
  }
  return response;
};

// 채점 결과 AI 수정안 생성 (저장은 별도 PATCH로 적용)
export const refineScoringResult = async (payload) => {
  return await apiRequestWithRetry('/api/scoring/refine', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, {
    maxRetries: 1,
    timeoutMs: calcApiTimeout('scoring-refine'),
  });
};

// 채점 기록 메타데이터 수정 (제목 등)
export const updateScoringRecordMeta = async (recordId, updates) => {
  return await apiRequest(`/api/scoring/history/${recordId}/meta`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
};

// 채점 기록의 원본 파일 미리보기 URL 발급
export const getScoringFilePreview = async (recordId, resultIndex) => {
  return await apiRequest(`/api/scoring/history/${recordId}/file/${resultIndex}/preview`);
};
